
import React, { useState, useEffect } from 'react';
import { Language, AppMode } from '../../types';
import { TRANSLATIONS } from '../../constants';

interface OnboardingTourProps {
  isOpen: boolean;
  onClose: () => void;
  language: Language;
  onNavigate: (mode: AppMode) => void;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({ isOpen, onClose, language, onNavigate }) => {
  const [step, setStep] = useState(0);
  const isRtl = language === 'ar';
  const t = TRANSLATIONS[language];

  const steps = [
    {
      title: t.tourTitle,
      desc: isRtl ? "مرحباً بك في منصة لهجة. دعنا نلقي نظرة سريعة على كيفية استخدام المنصة." : "Welcome to Lahja. Let's take a quick look at how to use the platform.",
      icon: 'fa-sparkles',
      color: 'emerald',
      mode: 'chat' as AppMode
    },
    {
      title: t.tourStep1Title,
      desc: t.tourStep1Desc,
      icon: 'fa-terminal',
      color: 'blue',
      mode: 'chat' as AppMode
    },
    {
      title: t.tourStep2Title,
      desc: t.tourStep2Desc,
      icon: 'fa-microphone-lines',
      color: 'rose',
      mode: 'live' as AppMode
    },
    {
      title: t.servicesTitle,
      desc: isRtl ? "استكشف خدمات الذكاء الاصطناعي المتقدمة مثل الدوبلاج والتحليل." : "Explore advanced AI services like dubbing and sentiment analysis.",
      icon: 'fa-cubes',
      color: 'amber',
      mode: 'services' as AppMode
    },
    {
      title: t.flowsTitle,
      desc: t.tourStep3Desc,
      icon: 'fa-diagram-project',
      color: 'indigo',
      mode: 'flows' as AppMode
    }
  ];

  useEffect(() => {
    if (isOpen) {
      onNavigate(steps[step].mode);
    }
  }, [step, isOpen]);

  if (!isOpen) return null;

  const next = () => {
    if (step < steps.length - 1) setStep(step + 1);
    else onClose();
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  const current = steps[step];

  return (
    <div className={`fixed z-[1000] transition-all duration-500 flex items-center justify-center pointer-events-none inset-0 p-4 ${step === 0 ? 'bg-slate-950/40 backdrop-blur-sm' : ''}`}>
      <div className={`
        pointer-events-auto bg-white dark:bg-[#111115] rounded-[28px] shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-slate-200 dark:border-white/10 overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 duration-300
        w-full max-w-[340px] flex flex-col
        ${step === 0 ? 'relative' : `fixed ${isRtl ? 'bottom-8 right-8' : 'bottom-8 left-8'} lg:${isRtl ? 'bottom-10 right-24' : 'bottom-10 left-24'}`}
      `}>
        
        {/* Progress Bar */}
        <div className="flex w-full h-1 bg-slate-100 dark:bg-white/5">
          {steps.map((_, i) => (
            <div key={i} className={`flex-1 transition-all duration-500 ${i <= step ? 'bg-emerald-500' : ''}`}></div>
          ))}
        </div>

        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
             <div className={`w-12 h-12 rounded-2xl bg-${current.color}-500/10 flex items-center justify-center text-xl text-${current.color}-500 shrink-0`}>
                <i className={`fas ${current.icon}`}></i>
             </div>
             <div>
                <h2 className="text-sm font-black text-slate-800 dark:text-white leading-tight uppercase tracking-wide">{current.title}</h2>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-0.5">Step {step + 1} of {steps.length}</p>
             </div>
          </div>

          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 leading-relaxed mb-6">
            {current.desc}
          </p>

          <div className="flex items-center justify-between gap-3">
             <button 
               onClick={onClose}
               className="text-[10px] font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
             >
                {t.tourSkip}
             </button>

             <div className="flex gap-2">
               {step > 0 && (
                 <button 
                   onClick={prev}
                   className="p-2.5 bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-300 rounded-xl hover:bg-slate-200 transition-all"
                 >
                    <i className={`fas fa-arrow-${isRtl ? 'left' : 'right'} text-[10px]`}></i>
                 </button>
               )}
               <button 
                 onClick={next}
                 className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-black rounded-xl shadow-lg shadow-emerald-500/20 active:scale-[0.95] transition-all text-[11px]"
               >
                  {step === steps.length - 1 ? t.tourFinish : t.tourNext}
               </button>
             </div>
          </div>
        </div>

        {/* Dynamic Context Helper */}
        <div className="px-6 py-2.5 bg-slate-50 dark:bg-white/[0.02] border-t border-slate-100 dark:border-white/5 flex items-center gap-2">
           <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
           <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider">
             {isRtl ? `استعراض صفحة ${current.mode}` : `Viewing ${current.mode} context`}
           </span>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
